import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/auth-context";
import { ROLE_LABELS } from "@/features/users/user-labels";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { PageHeader } from "@/components/ui/page";

export function ProfilePage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { user, logoutAll } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) {
    return null;
  }

  async function onConfirm() {
    setPending(true);
    setError(null);

    try {
      // Succeeds by ending this session too, so there is no state to reset
      // afterwards: the auth subscription sends us to the login screen.
      await logoutAll();
    } catch {
      setError(t("profileCloseError"));
      setPending(false);
      setConfirming(false);
    }
  }

  return (
    <>
      <PageHeader title={t("profileTitle")} />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader title={t("profileDetails")} />
          <CardBody>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-ink-muted">{tCommon("name")}</dt>
              <dd>{user.name}</dd>
              <dt className="text-ink-muted">{tCommon("email")}</dt>
              <dd>{user.email}</dd>
              <dt className="text-ink-muted">{tCommon("role")}</dt>
              <dd>{ROLE_LABELS[user.role]}</dd>
              <dt className="text-ink-muted">{tCommon("organization")}</dt>
              <dd>{user.organizationName ?? t("profileAccessAll")}</dd>
            </dl>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title={t("profileSecurity")} description={t("profileSecurityHint")} />
          <CardBody className="flex flex-col gap-3">
            {error ? <Alert>{error}</Alert> : null}

            <p className="text-sm text-ink-muted">{t("profileCloseHint")}</p>

            <div>
              <Button variant="danger" onClick={() => setConfirming(true)}>
                {t("closeAllSessions")}
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={confirming}
        title={t("closeAllSessions")}
        confirmLabel={t("closeAll")}
        destructive
        pending={pending}
        onConfirm={() => void onConfirm()}
        onCancel={() => {
          if (!pending) {
            setConfirming(false);
          }
        }}
      >
        <p>{t("profileConfirmBody")}</p>
      </ConfirmDialog>
    </>
  );
}
